/**
 * Menü-Index fuer die globale Suche (ProHandel-Portal).
 *
 * Quelle: die bereits vom MenuProvider geladenen Menues (headerMenu = Mega-Menue,
 * im ProHandel-Portal die einzige Quelle; menuData = Seitenmenue). Es wird
 * NICHTS nachgeladen — wir flachen nur die vorhandene Hierarchie ab und merken
 * uns pro Blatt den Breadcrumb ("Auftrag › Stammdaten › Touren").
 *
 * Struktur der Menue-Items siehe providers/menu-provider.jsx (convertMenuItems):
 *   { title, path, children, disabled, target }  bzw. { heading, type:'header' }
 */

/** Kleinbuchstaben, Umlaute/Akzente gefaltet, Mehrfach-Whitespace reduziert. */
export function normalize(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Hierarchie → flache Liste navigierbarer Eintraege.
 * Ueberschriften (heading-only) fliessen als Zwischenebene in den Breadcrumb
 * der nachfolgenden Geschwister ein. Deaktivierte ("Soon") und Eintraege ohne
 * Pfad werden nicht als Treffer aufgenommen, ihre Kinder schon.
 */
export function buildMenuIndex(...menus) {
  const out = [];
  const seen = new Set();

  const walk = (items, trail) => {
    if (!Array.isArray(items)) return;
    let heading = null;
    for (const item of items) {
      if (!item) continue;
      if (item.type === 'header' || (item.heading && !item.title)) {
        heading = item.heading || null;
        continue;
      }
      const title = (item.title || '').trim();
      if (!title) continue;
      const parents = heading ? [...trail, heading] : trail;
      const hasChildren = Array.isArray(item.children) && item.children.length > 0;
      const path = typeof item.path === 'string' ? item.path.trim() : '';
      if (path && path !== '#' && !item.disabled && !seen.has(path)) {
        seen.add(path);
        out.push({
          key: `menu-${item.id ?? path}`,
          title,
          path,
          target: item.target,
          breadcrumb: parents,
          nTitle: normalize(title),
          nTrail: normalize(parents.join(' ')),
        });
      }
      if (hasChildren) walk(item.children, [...parents, title]);
    }
  };

  for (const menu of menus) walk(menu, []);
  return out;
}

/** true, wenn alle Zeichen von `needle` in Reihenfolge in `hay` vorkommen. */
function isSubsequence(needle, hay) {
  let i = 0;
  for (let j = 0; j < hay.length && i < needle.length; j += 1) {
    if (hay[j] === needle[i]) i += 1;
  }
  return i === needle.length;
}

/** Score eines Tokens gegen den Titel (0 = kein Treffer). */
function scoreToken(token, entry) {
  const t = entry.nTitle;
  if (t === token) return 100;
  if (t.startsWith(token)) return 80;
  if (t.split(/[\s\-/›>.(]+/).some((w) => w.startsWith(token))) return 65;
  if (t.includes(token)) return 50;
  if (entry.nTrail.includes(token)) return 25; // Treffer nur im Pfad
  if (token.length >= 3 && isSubsequence(token, t)) return 12; // Tippfehler-tolerant ("trn" → "Touren")
  return 0;
}

/**
 * Fuzzy-Suche: jedes Wort der Anfrage muss irgendwo (Titel oder Pfad) passen.
 * Sortierung nach Score, dann kurzer Breadcrumb zuerst, dann alphabetisch.
 */
export function searchMenu(index, query, limit = 5) {
  const tokens = normalize(query).split(' ').filter(Boolean);
  if (!tokens.length) return [];
  const hits = [];
  for (const entry of index) {
    let total = 0;
    let ok = true;
    for (const token of tokens) {
      const s = scoreToken(token, entry);
      if (!s) {
        ok = false;
        break;
      }
      total += s;
    }
    if (ok) hits.push({ entry, score: total });
  }
  hits.sort(
    (a, b) =>
      b.score - a.score ||
      a.entry.breadcrumb.length - b.entry.breadcrumb.length ||
      a.entry.title.localeCompare(b.entry.title, 'de'),
  );
  return hits.slice(0, limit).map((h) => h.entry);
}
